/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { makeDefaultFilter } from 'api/Filter';

import { ia, iaRa } from 'lib/helpers/utility';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useOperatingSystem } from 'lib/hooks/useOperatingSystem';

import TooltipContent from '../components/TooltipContent';
import { checkActiveItem } from '../lib/checkActiveItem';

export function useViewTabs({
  title,
  items,
  category,
  updateFilters,
  resetFilters,
  removeDefaults = false,
  openModal,
  openEditModal
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view');
  const type = searchParams.get('type');
  const os = useOperatingSystem();

  const navigate = useNavigate();
  const location = useLocation();

  const activeItem = useMemo(
    () => checkActiveItem(items, view, location, type),
    [items, location, view, type]
  );

  const onSwitchItemClick = (item) => {
    if (item.navigate) {
      navigate(item.navigate + (item.type ? `?type=${item.type}` : ''), {
        state: { disableDefaultFilter: true }
      });
      resetFilters();
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('type');

    if (item?.value === null) {
      newParams.delete('view');
      resetFilters();
    } else {
      newParams.set('view', item.value);
    }

    setSearchParams(newParams);

    navigate(location.pathname + '?' + newParams.toString(), {
      state: { disableDefaultFilter: true }
    });
  };

  const { data: filtersData } = useFilters({
    params: { category },
    dependencies: [category]
  });

  const onFilterClick = (filter) => {
    setSearchParams((prev) => {
      if (filter.name === null) {
        prev.delete('view');
        updateFilters(null);

        return prev;
      }
      const selectedFilter = iaRa(filtersData?.filters).find(
        (f) => f.name === filter.name && f.category === category
      );

      if (filter.type) {
        prev.set('type', filter.type);
      }
      prev.set('view', filter.name);

      if (!selectedFilter) return prev;

      updateFilters(selectedFilter);
      return prev;
    });
  };

  const { mutate: makeDefaultFilterHandler, isLoading: isMakingDefaultFilter } = useCustomMutation({
    mutationFn: (params) => makeDefaultFilter(navigate, params),
    successMessage: 'Filter set as default successfully',
    errorMessage: 'Failed to set filter as default. Please try again later.',
    invalidateQueryKey: ['default-filters', ['filters', category]]
  });

  const hasNavigate = iaRa(items).some((item) => (!!item.navigate && !!item.type) || !item.value);

  const filtered = [
    ...(!hasNavigate
      ? [
          {
            label: `All ${title || category}`,
            value: null
          }
        ]
      : []),
    ...items
  ];
  const removeItems = removeDefaults && !ia(filtersData?.filters) ? [] : filtered;

  const toggleItems = useMemo(() => {
    let firstActionAdded = false;
    return iaRa(removeItems)
      .map((item) => ({
        ...item,
        onClick: () => onSwitchItemClick(item)
      }))
      .concat(
        iaRa(filtersData?.filters).map((filter) => {
          const item = {
            label: filter.name,
            value: filter.name,
            default: filter.is_default,
            onClick: () => onFilterClick(filter),
            className: 'px-2 hover:bg-primary-50',
            actions: [
              {
                label: 'Rename View',
                value: 'edit',
                icon: 'editor-firstline3rec-mono',
                onClick: () => openEditModal(filter),
                textClassName: 'text-neutral-900'
              },
              {
                label: filter.is_default ? 'Unset as default view' : 'Set as default view',
                value: 'edit',
                icon: 'star-1-mono',
                onClick: () => {
                  if (isMakingDefaultFilter) return;
                  makeDefaultFilterHandler({
                    id: filter.id,
                    category,
                    isDefault: !filter.is_default
                  });
                },
                textClassName: 'text-neutral-900'
              },
              {
                label: 'Delete',
                value: 'delete',
                icon: 'trash-mono',
                onClick: () => openModal({ id: filter.id, name: filter.name }),
                textClassName: 'text-neutral-900'
              }
            ]
          };
          if (!firstActionAdded && item.actions) {
            item.split = true;
            firstActionAdded = true;
          }
          return item;
        })
      )
      .map((item, index) => ({
        ...item,
        tooltip: <TooltipContent label={item.label} index={index} os={os} />
      }));
  }, [filtersData, items, category, location]);

  return {
    view,
    activeItem,
    filtersData,
    onSwitchItemClick,
    onFilterClick,
    makeDefaultFilterHandler,
    isMakingDefaultFilter,
    removeItems,
    toggleItems
  };
}
